import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Calendar, CheckCircle2, Plus, Trash2, Receipt, Play, TrendingUp,
  ArrowRight, PlayCircle,
} from "lucide-react";
import { toast } from "sonner";
import {
  LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import JeaCard from "@/components/jea/JeaCard";
import JeaButton from "@/components/jea/JeaButton";
import JeaInput from "@/components/jea/JeaInput";
import JeaSelect from "@/components/jea/JeaSelect";
import MoneyInput from "@/components/jea/MoneyInput";
import PillButton from "@/components/jea/PillButton";
import Skeleton from "@/components/jea/Skeleton";
import EmptyState from "@/components/jea/EmptyState";
import RunQueryModal from "@/components/queries/RunQueryModal";
import { PortfolioHeatmapPanel } from "@/components/monitoring/PortfolioHeatmapPanel";
import { apiClient, formatApiError } from "@/lib/apiClient";
import { formatMoney, formatDate, TIERS } from "@/lib/pricing";
import { usePreviewGate } from "@/hooks/usePreviewGate";
import { useRolePermissions } from "@/hooks/useRolePermissions";
import { cn } from "@/lib/utils";

const COST_CATEGORIES = [
  { value: "filing_fee", label: "Filing Fee" },
  { value: "process_server", label: "Process Server" },
  { value: "court_cost", label: "Court Cost" },
  { value: "recording_fee", label: "Recording Fee" },
  { value: "attorney_fee", label: "Attorney Fee" },
  { value: "other", label: "Other" },
];

const urgencyColor = (days) => {
  if (days == null) return "#5A7A9A";
  if (days <= 30) return "#E74C3C";
  if (days <= 60) return "#F39C12";
  return "#5A7A9A";
};

const DeadlineList = ({ deadlines, loading }) => {
  const nav = useNavigate();
  return (
    <JeaCard data-testid="monitoring-deadlines">
      <h2 className="text-white font-bold text-[16px]">Upcoming Deadlines</h2>
      <p className="text-text-secondary text-[13px] mb-4">Chronological across all active judgments.</p>
      {loading ? (
        <Skeleton className="h-24" />
      ) : deadlines.length === 0 ? (
        <div className="flex flex-col items-center py-8" data-testid="deadlines-empty">
          <CheckCircle2 size={40} className="text-success mb-2" />
          <div className="text-text-secondary text-[14px]">No upcoming deadlines within 90 days.</div>
        </div>
      ) : (
        <div className="space-y-2">
          {deadlines.map((d, i) => {
            const c = urgencyColor(d.days_until);
            return (
              <div
                key={`${d.debtor_id}-${d.type}-${i}`}
                className="flex items-center gap-3 py-2 pl-3 pr-3 border-l-4 rounded-r-[6px] bg-bg-tertiary/40"
                style={{ borderLeftColor: c }}
                data-testid={`deadline-row-${i}`}
              >
                <Calendar size={16} style={{ color: c }} className="shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-white text-[14px] font-semibold">{d.debtor_name}</div>
                  <div className="text-text-secondary text-[13px]">{d.label}</div>
                </div>
                <div className="jea-mono text-[13px]" style={{ color: c }}>{formatDate(d.date)}</div>
                <span
                  className="inline-flex px-2 py-1 rounded-full text-[11px] font-bold tracking-[0.06em]"
                  style={{ background: `${c}22`, color: c, border: `1px solid ${c}55` }}
                >
                  {d.days_until}d
                </span>
                <button
                  type="button"
                  onClick={() => nav(`/debtors/${d.debtor_id}`)}
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-[8px] border border-accent-pink text-accent-pink hover:bg-accent-pink hover:text-bg-primary text-[12px] font-semibold transition-colors"
                  data-testid={`deadline-take-action-${i}`}
                >
                  Take Action <ArrowRight size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </JeaCard>
  );
};

// Simple SVG timeline visualization
const TimelineViz = ({ debtors, loading }) => {
  const nav = useNavigate();
  const now = new Date();
  const parseDate = (s) => (s ? new Date(s) : null);

  const rows = useMemo(() => {
    return debtors
      .map((d) => ({
        ...d,
        entered: parseDate(d.date_entered),
        expires: parseDate(d.judgment_expires),
      }))
      .filter((r) => r.entered && r.expires);
  }, [debtors]);

  if (loading) return <JeaCard><Skeleton className="h-56" /></JeaCard>;
  if (rows.length === 0) {
    return (
      <JeaCard data-testid="timeline-empty">
        <h2 className="text-white font-bold text-[16px]">Portfolio Judgment Timeline</h2>
        <div className="text-text-secondary text-[13px] mt-3">Add active judgments to see the portfolio timeline.</div>
      </JeaCard>
    );
  }

  const minDate = new Date(Math.min(...rows.map((r) => r.entered.getTime())));
  const maxDate = new Date(Math.max(...rows.map((r) => r.expires.getTime())));
  const totalMs = maxDate.getTime() - minDate.getTime() || 1;
  const width = 800;
  const barH = 14;
  const gap = 12;
  const height = rows.length * (barH + gap) + 40;
  const nameW = 140;
  const chartW = width - nameW - 20;

  const xOf = (d) => nameW + ((d.getTime() - minDate.getTime()) / totalMs) * chartW;

  return (
    <JeaCard data-testid="portfolio-timeline">
      <h2 className="text-white font-bold text-[16px]">Portfolio Judgment Timeline</h2>
      <p className="text-text-secondary text-[13px] mb-4">
        Each bar = one judgment's lifespan. Green safe → amber approaching → red final 30 days.
      </p>
      <div className="overflow-x-auto">
        <svg width={width} height={height} data-testid="timeline-svg">
          {/* today line */}
          <line x1={xOf(now)} y1={0} x2={xOf(now)} y2={height} stroke="#fb6f92" strokeDasharray="4 4" strokeWidth={2} />
          <text x={xOf(now) + 4} y={12} fill="#fb6f92" fontSize={10}>Today</text>

          {rows.map((r, i) => {
            const y = 24 + i * (barH + gap);
            const x0 = xOf(r.entered);
            const x1 = xOf(r.expires);
            const total = x1 - x0;
            const safe = total * 0.5;
            const warn = total * 0.75;
            const urgent = total * 0.9;
            return (
              <g
                key={r.debtor_id}
                className="cursor-pointer"
                onClick={() => nav(`/debtors/${r.debtor_id}`)}
                data-testid={`timeline-bar-${r.debtor_id}`}
              >
                <text x={0} y={y + barH - 2} fontSize={12} fill="#A8B8D0">{(r.debtor_name || "").slice(0, 18)}</text>
                {/* green safe segment */}
                <rect x={x0} y={y} width={safe} height={barH} rx={6} fill="#2ECC71" opacity={0.85} />
                {/* amber */}
                <rect x={x0 + safe} y={y} width={warn - safe} height={barH} fill="#F39C12" opacity={0.85} />
                {/* red urgent */}
                <rect x={x0 + urgent} y={y} width={total - urgent} height={barH} rx={6} fill="#E74C3C" opacity={0.85} />

                {/* renewal marker (accent diamond above bar) */}
                {r.renewal_deadline && (
                  <polygon
                    points={`${xOf(parseDate(r.renewal_deadline))},${y - 2} ${xOf(parseDate(r.renewal_deadline)) - 5},${y - 8} ${xOf(parseDate(r.renewal_deadline)) + 5},${y - 8}`}
                    fill="#fb6f92"
                  />
                )}
                {/* lien renewal marker (silver-blue diamond below bar) */}
                {r.lien_expires && (
                  <polygon
                    points={`${xOf(parseDate(r.lien_expires))},${y + barH + 2} ${xOf(parseDate(r.lien_expires)) - 5},${y + barH + 8} ${xOf(parseDate(r.lien_expires)) + 5},${y + barH + 8}`}
                    fill="#A8B8D0"
                  />
                )}
                <title>{r.debtor_name} · {formatDate(r.date_entered)} → {formatDate(r.judgment_expires)}</title>
              </g>
            );
          })}
        </svg>
      </div>
      <div className="mt-3 flex items-center flex-wrap gap-4 text-[12px]">
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-success" /> Safe</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-warning" /> Approaching</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-danger" /> Final 30 days</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rotate-45 bg-accent-pink" /> Renewal deadline</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rotate-45 bg-text-secondary" /> Lien renewal</span>
      </div>
    </JeaCard>
  );
};

const InterestPanel = ({ summary, loading }) => {
  if (loading || !summary) return <JeaCard><Skeleton className="h-40" /></JeaCard>;
  const series = (summary.time_series || []).map((s) => ({ ...s, value: s.value_cents / 100 }));
  return (
    <JeaCard data-testid="portfolio-interest">
      <h2 className="text-white font-bold text-[16px] flex items-center gap-2">
        <TrendingUp size={18} className="text-accent-pink" /> Portfolio Interest Accrued
      </h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
        <div>
          <div className="text-text-muted text-[10px] uppercase tracking-[0.08em]">Original Judgment Total</div>
          <div className="jea-mono text-accent-pink font-bold text-[18px]">{formatMoney(summary.total_principal_cents)}</div>
        </div>
        <div>
          <div className="text-text-muted text-[10px] uppercase tracking-[0.08em]">Total Interest Accrued</div>
          <div className="jea-mono text-success font-bold text-[18px]">{formatMoney(summary.total_accrued_cents)}</div>
        </div>
        <div>
          <div className="text-text-muted text-[10px] uppercase tracking-[0.08em]">Current Total Value</div>
          <div className="jea-mono text-accent-pink font-extrabold text-[24px]">{formatMoney(summary.current_total_cents)}</div>
        </div>
        <div>
          <div className="text-text-muted text-[10px] uppercase tracking-[0.08em]">Daily Accrual Rate</div>
          <div className="jea-mono text-text-secondary font-bold text-[18px]">{formatMoney(summary.daily_rate_cents)}</div>
        </div>
      </div>
      <div className="h-48 mt-4">
        <ResponsiveContainer>
          <LineChart data={series}>
            <CartesianGrid stroke="#1E3A58" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#A8B8D0" }} />
            <YAxis tick={{ fontSize: 10, fill: "#A8B8D0" }} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
            <Tooltip
              contentStyle={{ background: "#0D1E36", border: "1px solid #1E3A58", borderRadius: 8, fontSize: 12 }}
              formatter={(v) => `$${Number(v).toLocaleString()}`}
            />
            <Line type="monotone" dataKey="value" stroke="#fb6f92" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      {summary.rate_sources?.length > 0 && (
        <div className="mt-4">
          <div className="jea-caption mb-2">Interest Rate Sources</div>
          <ul className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 text-[12px]">
            {summary.rate_sources.map((r) => (
              <li key={r.state} className="text-text-secondary">
                <span className="jea-mono">{r.state}</span> — {r.rate_pct}%
              </li>
            ))}
          </ul>
        </div>
      )}
    </JeaCard>
  );
};

const CostsPanel = ({ debtorFilter, debtors }) => {
  const { canMutateDebtors } = useRolePermissions();
  const [costs, setCosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({
    debtor_id: debtorFilter || (debtors[0]?.id ?? ""),
    date: new Date().toISOString().slice(0, 10),
    description: "",
    category: "filing_fee",
    amount_cents: 0,
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      if (debtorFilter) {
        const { data } = await apiClient.get(`/debtors/${debtorFilter}/costs`);
        setCosts((data.costs || []).map((c) => ({ ...c, debtor_name: debtors.find((d) => d.id === debtorFilter)?.full_name })));
        setTotal(data.total_cents || 0);
      } else {
        const { data } = await apiClient.get("/monitoring/costs-summary");
        setCosts(data.costs || []);
        setTotal(data.total_cents || 0);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [debtorFilter]);

  const addCost = async () => {
    if (!form.debtor_id) { toast.error("Select a debtor first."); return; }
    if (!form.description.trim()) { toast.error("Description required."); return; }
    if (!form.amount_cents) { toast.error("Amount required."); return; }
    setSaving(true);
    try {
      await apiClient.post(`/debtors/${form.debtor_id}/costs`, {
        date: form.date,
        description: form.description.trim(),
        category: form.category,
        amount_cents: form.amount_cents,
      });
      toast.success("Cost logged.");
      setAddOpen(false);
      setForm((f) => ({ ...f, description: "", amount_cents: 0 }));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const del = async (row) => {
    if (!row.debtor_id) return;
    try {
      await apiClient.delete(`/debtors/${row.debtor_id}/costs/${row.id}`);
      toast.success("Removed.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <JeaCard data-testid="recoverable-costs">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div>
          <h2 className="text-white font-bold text-[16px]">Recoverable Costs</h2>
          <p className="text-text-secondary text-[13px] mt-1">
            Log court filing fees, process server fees, and other costs recoverable under state statute.
          </p>
        </div>
        {canMutateDebtors && (
          <JeaButton
            onClick={() => setAddOpen(true)}
            className="gap-2"
            data-testid="costs-add-open"
          >
            <Plus size={14} /> Add Cost
          </JeaButton>
        )}
      </div>

      {addOpen && canMutateDebtors && (
        <div className="rounded-[8px] bg-bg-primary border border-border-default p-4 mb-3 space-y-3" data-testid="costs-add-form">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            {!debtorFilter && (
              <div>
                <div className="jea-caption mb-1">Judgment</div>
                <JeaSelect
                  value={form.debtor_id}
                  onChange={(v) => setForm((f) => ({ ...f, debtor_id: v }))}
                  options={debtors.map((d) => ({ value: d.id, label: d.full_name }))}
                  data-testid="costs-form-debtor"
                />
              </div>
            )}
            <div>
              <div className="jea-caption mb-1">Date</div>
              <JeaInput type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} data-testid="costs-form-date" />
            </div>
            <div className="md:col-span-2">
              <div className="jea-caption mb-1">Description</div>
              <JeaInput
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="e.g. Superior Court filing fee"
                data-testid="costs-form-description"
              />
            </div>
            <div>
              <div className="jea-caption mb-1">Category</div>
              <JeaSelect
                value={form.category}
                onChange={(v) => setForm((f) => ({ ...f, category: v }))}
                options={COST_CATEGORIES}
                data-testid="costs-form-category"
              />
            </div>
            <div>
              <div className="jea-caption mb-1">Amount</div>
              <MoneyInput
                valueCents={form.amount_cents}
                onChange={(c) => setForm((f) => ({ ...f, amount_cents: c }))}
                testid="costs-form-amount"
              />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => setAddOpen(false)} className="text-text-secondary text-[13px] hover:text-white px-3" data-testid="costs-form-cancel">Cancel</button>
            <JeaButton size="sm" onClick={addCost} loading={saving} data-testid="costs-form-save">Save</JeaButton>
          </div>
        </div>
      )}

      {loading ? (
        <Skeleton className="h-24" />
      ) : costs.length === 0 ? (
        <EmptyState icon={Receipt} title="No recoverable costs logged yet." testid="costs-empty" />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="costs-table">
            <thead>
              <tr className="border-b border-border-default">
                {["Date", "Judgment", "Description", "Category", "Amount", ""].map((h) => (
                  <th key={h} className="jea-caption text-text-secondary p-3 text-left">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {costs.map((r) => (
                <tr key={r.id} className="border-b border-border-default" data-testid={`cost-row-${r.id}`}>
                  <td className="p-3 jea-date text-text-secondary text-[13px]">{formatDate(r.date)}</td>
                  <td className="p-3 text-white text-[13px]">{r.debtor_name || "—"}</td>
                  <td className="p-3 text-text-secondary text-[13px]">{r.description}</td>
                  <td className="p-3 text-text-secondary text-[13px] capitalize">
                    {(r.category || "").replace(/_/g, " ")}
                  </td>
                  <td className="p-3 jea-mono text-accent-pink font-semibold">{formatMoney(r.amount_cents)}</td>
                  <td className="p-3">
                    {canMutateDebtors ? (
                      <button
                        type="button"
                        onClick={() => del(r)}
                        className="text-text-secondary hover:text-danger"
                        data-testid={`cost-del-${r.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    ) : (
                      <span className="text-text-muted text-[12px]">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={4} />
                <td className="p-3 text-accent-pink jea-mono font-extrabold text-[16px]" data-testid="costs-total">
                  {formatMoney(total)}
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      <div className="mt-3 text-text-muted text-[12px]">
        Costs can be added to the judgment amount with proper documentation and court order.
      </div>
    </JeaCard>
  );
};

const AssetScheduler = ({ debtor, onSchedule, onRunSearch }) => {
  const [saving, setSaving] = useState(false);
  const nav = useNavigate();
  const { guardAction } = usePreviewGate();
  const { canMutateDebtors, canRunQueries } = useRolePermissions();
  const tier = debtor?.tier || "bronze";
  const label = { bronze: "Every 12 months", silver: "Every 6 months", gold: "Every 3 months" }[tier] || "—";

  const setTier = async (t) => {
    setSaving(true);
    try {
      await apiClient.patch(`/debtors/${debtor.id}`, { tier: t });
      toast.success("Query schedule updated.");
      onSchedule?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const runNow = guardAction(() => onRunSearch?.(debtor), { feature: "running queries" });

  return (
    <JeaCard data-testid="asset-scheduler">
      <h2 className="text-white font-bold text-[16px]">Asset Search Schedule</h2>
      <p className="text-text-secondary text-[13px] mt-1">
        Current: <span className="text-white font-semibold">{label}</span>
      </p>
      {canMutateDebtors && (
        <div className="mt-3 flex gap-2 flex-wrap">
          {TIERS.map((t) => (
            <PillButton
              key={t.key}
              active={tier === t.key}
              onClick={() => setTier(t.key)}
              data-testid={`scheduler-tier-${t.key}`}
            >
              {t.frequency}
            </PillButton>
          ))}
        </div>
      )}
      <div className="mt-4">
        {canRunQueries && (
          <JeaButton
            onClick={runNow}
            fullWidth
            className="gap-2"
            data-testid="scheduler-run-now"
          >
            <PlayCircle size={18} /> Run Search Now
          </JeaButton>
        )}
        <button
          type="button"
          onClick={() => nav(`/debtors/${debtor.id}`)}
          className={cn("w-full text-center text-text-secondary hover:text-accent-pink text-[12px] font-medium", canRunQueries ? "mt-2" : "mt-0")}
          data-testid="scheduler-open-profile"
        >
          Open Judgment Profile →
        </button>
      </div>
    </JeaCard>
  );
};

const MonitoringPage = () => {
  const [debtors, setDebtors] = useState([]);
  const [deadlines, setDeadlines] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDebtor, setSelectedDebtor] = useState("");
  const [mode, setMode] = useState("portfolio");
  const [runQuery, setRunQuery] = useState({ open: false, debtor: null });

  const load = async () => {
    setLoading(true);
    try {
      const [d, dl, tl, sm] = await Promise.all([
        apiClient.get("/debtors"),
        apiClient.get("/monitoring/deadlines"),
        apiClient.get("/monitoring/portfolio-timeline"),
        apiClient.get("/monitoring/interest-summary"),
      ]);
      setDebtors((d.data.debtors || []).filter((x) => x.status !== "archived"));
      setDeadlines(dl.data.deadlines || []);
      setTimeline(tl.data.debtors || []);
      setSummary(sm.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filteredTimeline = selectedDebtor
    ? timeline.filter((r) => r.debtor_id === selectedDebtor)
    : timeline;
  const filteredDeadlines = selectedDebtor
    ? deadlines.filter((r) => r.debtor_id === selectedDebtor)
    : deadlines;
  const currentDebtor = debtors.find((d) => d.id === selectedDebtor);

  return (
    <div className="space-y-6" data-testid="monitoring-page">
      <div className="space-y-1">
        <div className="jea-caption">Portfolio</div>
        <h1 className="text-[32px] font-extrabold tracking-[-0.02em] text-white">Monitoring</h1>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="min-w-[280px]">
          <div className="jea-caption mb-1">Judgment Filter</div>
          <JeaSelect
            value={selectedDebtor}
            onChange={setSelectedDebtor}
            options={[
              { value: "", label: "All Active Judgments" },
              ...debtors.map((d) => ({ value: d.id, label: d.full_name })),
            ]}
            data-testid="monitoring-debtor-filter"
          />
        </div>
        <div className="flex gap-2" data-testid="monitoring-mode-toggle">
          <PillButton active={mode === "portfolio"} onClick={() => setMode("portfolio")} data-testid="monitoring-mode-portfolio">
            Portfolio View
          </PillButton>
          <PillButton active={mode === "detailed"} onClick={() => setMode("detailed")} data-testid="monitoring-mode-detailed">
            Detailed View
          </PillButton>
        </div>
      </div>

      <DeadlineList deadlines={filteredDeadlines} loading={loading} />
      {mode === "portfolio" && <PortfolioHeatmapPanel />}
      <TimelineViz debtors={filteredTimeline} loading={loading} />
      <InterestPanel summary={summary} loading={loading} />
      <CostsPanel debtorFilter={selectedDebtor} debtors={debtors} />

      {mode === "detailed" && currentDebtor && (
        <AssetScheduler
          debtor={currentDebtor}
          onSchedule={load}
          onRunSearch={(d) => setRunQuery({ open: true, debtor: d })}
        />
      )}

      <RunQueryModal
        open={runQuery.open}
        debtor={runQuery.debtor}
        onClose={() => setRunQuery({ open: false, debtor: null })}
        onCompleted={() => { setRunQuery({ open: false, debtor: null }); load(); }}
      />
    </div>
  );
};

export default MonitoringPage;
